/**
 * Lazy expand TreeTable: parent keys in `{tableName}_params` (Swing prepareExpandNodeParam).
 */
import {
  mergeTreeTableExportColumns,
  type TreeTableGetDataColumn,
} from '@/common/utils/treeTableGetData'

export type TreeTableParentColumnMapping = {
  columnName: string
  parentField: string
  columnType?: string
}

export type TreeTableExpandParamField = {
  value: unknown
  type: string
}

function toPlainVal(val: unknown): unknown {
  if (val != null && typeof val === 'object' && (val as { __v_isRef?: boolean }).__v_isRef === true) {
    return toPlainVal((val as { value: unknown }).value)
  }
  if (
    val != null &&
    typeof val === 'object' &&
    !('value' in val) &&
    ('_value' in val || '_rawValue' in val)
  ) {
    const innerValue =
      (val as { _value?: unknown; _rawValue?: unknown })._value ??
      (val as { _rawValue?: unknown })._rawValue
    if (innerValue === null || ['string', 'number', 'boolean'].includes(typeof innerValue)) {
      return innerValue
    }
  }
  if (val != null && typeof val === 'object' && 'value' in val && Object.keys(val as object).length <= 2) {
    return (val as { value: unknown }).value
  }
  return val
}

function extractCellValueAndType(cell: unknown): { value: unknown; type?: string } {
  if (cell != null && typeof cell === 'object' && 'value' in (cell as object)) {
    const typed = cell as { value: unknown; type?: string }
    const nested = extractCellValueAndType(typed.value)
    return { value: nested.value, type: typed.type ?? nested.type }
  }
  return { value: toPlainVal(cell) }
}

function resolveExpandRowCell(row: Record<string, unknown>, name: string): unknown {
  const rowData = row._rowData as Record<string, unknown> | undefined
  const candidates = [
    row[name],
    (row.fields as Record<string, unknown> | undefined)?.[name],
    (rowData?.fields as Record<string, unknown> | undefined)?.[name],
    rowData?.[name],
  ]
  for (const candidate of candidates) {
    if (candidate !== undefined) return candidate
  }
  return undefined
}

function normalizeClientFieldType(valueType: string | undefined, value: unknown): string {
  const normalized = String(valueType || '').trim()
  if (normalized === 'Checkbox') return 'Boolean'
  if (normalized && normalized !== 'Text' && normalized !== 'Identificator') {
    return normalized
  }
  if (
    value != null &&
    typeof value === 'object' &&
    (Array.isArray((value as { rows?: unknown[] }).rows) ||
      Array.isArray((value as { value?: { rows?: unknown[] } }).value?.rows))
  ) {
    return 'UniDataTable'
  }
  if (typeof value === 'boolean') return 'Boolean'
  if (typeof value === 'number') return Number.isInteger(value) ? 'Long' : 'Double'
  return 'String'
}

function toClientFieldValue(value: unknown, valueType?: string): unknown {
  const v = toPlainVal(value)
  const normalizedType = String(valueType || '').trim()
  const isUniDataTableValue =
    normalizedType === 'UniDataTable' ||
    normalizedType === 'UniDataTableFields' ||
    (v != null &&
      typeof v === 'object' &&
      (Array.isArray((v as { rows?: unknown[] }).rows) ||
        Array.isArray((v as { value?: { rows?: unknown[] } }).value?.rows)))

  if (v === null || v === undefined) {
    if (normalizedType === 'Boolean' || normalizedType === 'Checkbox') return false
    if (isUniDataTableValue) return { rows: [] }
    return ''
  }
  if (v === 'undefined') return ''
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v
  if (isUniDataTableValue) return v
  return String(v)
}

export function resolveTreeTableColumnsForContract(
  configColumns: TreeTableGetDataColumn[],
  runtimeColumns: TreeTableGetDataColumn[] = [],
): TreeTableGetDataColumn[] {
  return mergeTreeTableExportColumns(configColumns, runtimeColumns)
}

/** Колонки с likeValueParentId + parentColumn из метаданных формы. */
export function getTreeTableParentColumnMappings(
  columns: TreeTableGetDataColumn[],
): TreeTableParentColumnMapping[] {
  const out: TreeTableParentColumnMapping[] = []
  for (const c of columns) {
    const name = c.name
    if (!name) continue
    if (c.likeValueParentId && c.parentColumn) {
      out.push({ columnName: name, parentField: c.parentColumn, columnType: c.type })
    }
  }
  return out
}

/**
 * Поля одной строки UniDataTable для `{tableName}_params`.
 */
export function buildTreeTableExpandParamsFields(
  row: Record<string, unknown>,
  columns: TreeTableGetDataColumn[],
): Record<string, TreeTableExpandParamField> {
  const fields: Record<string, TreeTableExpandParamField> = {}
  const mappings = getTreeTableParentColumnMappings(columns)
  if (mappings.length === 0) {
    return fields
  }

  for (const m of mappings) {
    const raw = extractCellValueAndType(resolveExpandRowCell(row, m.columnName))
    const plainValue = toClientFieldValue(raw.value, raw.type ?? m.columnType)
    fields[m.parentField] = {
      value: plainValue,
      type: normalizeClientFieldType(raw.type ?? m.columnType, plainValue),
    }
  }
  return fields
}
